import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Typography, TextField, Button, IconButton, InputAdornment, Divider } from "@mui/material";

import { updatePassword } from "../../../../actions/user";
import { notificationAdd } from "../../../../actions/notification";

// css module
import styles from "./security-settings.module.scss";

const SecuritySettings = (): React.JSX.Element => {
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  const [formValues, setFormValues] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  const toggleShowPassword = (field: "oldPassword" | "newPassword" | "confirmPassword") => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleInputChange =
    (field: "oldPassword" | "newPassword" | "confirmPassword") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleChangePassword = () => {
    try {
      dispatch(updatePassword(formValues));
      setFormValues({
        oldPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getMilliseconds(),
          variant: "error",
          message: err?.message || "Something went wrong while updating password!",
          autoTimeout: true
        })
      );
    }
  };

  return (
    <div className={styles.securityCard}>
      <div className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          Security Settings
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Manage your password and account security preferences
        </Typography>
      </div>

      <Divider className={styles.divider} />

      <div className={styles.form}>
        <Typography variant="subtitle1" className={styles.sectionTitle}>
          Change Password
        </Typography>

        <TextField
          variant="outlined"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => toggleShowPassword("oldPassword")} edge="end">
                    {showPassword.oldPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
          label="Current Password"
          placeholder="Enter your current password"
          type={showPassword.oldPassword ? "text" : "password"}
          size="small"
          fullWidth
          value={formValues.oldPassword}
          onChange={handleInputChange("oldPassword")}
        />

        <TextField
          variant="outlined"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => toggleShowPassword("newPassword")} edge="end">
                    {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
          label="New Password"
          placeholder="Enter your new password"
          type={showPassword.newPassword ? "text" : "password"}
          size="small"
          fullWidth
          value={formValues.newPassword}
          onChange={handleInputChange("newPassword")}
        />

        <TextField
          variant="outlined"
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => toggleShowPassword("confirmPassword")} edge="end">
                    {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
          label="Confirm New Password"
          placeholder="Confirm your new password"
          type={showPassword.confirmPassword ? "text" : "password"}
          size="small"
          fullWidth
          value={formValues.confirmPassword}
          onChange={handleInputChange("confirmPassword")}
        />
      </div>

      <div className={styles.actions}>
        <Button variant="contained" color="primary" onClick={handleChangePassword}>
          Update Password
        </Button>
      </div>
    </div>
  );
};

export default SecuritySettings;
